// SELECTOR: CSS selector to find all types of chips (merge tags, variable tags, spin tags, date tags, error tags)
const CHIP_SELECTOR =
  '.merge-tag-chip-container, .variable-tag-chip-container, .spin-tag-chip-container, .date-tag-chip-container, .error-chip-container';

// TRACKED_STYLES: CSS properties that we want chips to inherit from surrounding text
const TRACKED_STYLES = ['color', 'backgroundColor', 'fontSize', 'fontFamily'];

// FORMAT_TAGS: HTML tags used for text formatting (bold, italic, underline)
const FORMAT_TAGS = { bold: 'strong', italic: 'em', underline: 'u' } as const;

/**
 * FUNCTION: getStyles
 * PURPOSE: Extracts inline CSS styles from a span element
 *
 * WHY: We need to read the current formatting from text elements to apply it to chips
 *
 * @param el - The HTML element to extract styles from
 * @returns Object containing the tracked CSS properties and their values
 *
 * EXAMPLE: If a span has color: red and fontSize: 16px, returns { color: 'red', fontSize: '16px' }
 */
const getStyles = (el: any) =>
  el?.tagName === 'SPAN'
    ? TRACKED_STYLES.reduce(
        // eslint-disable-next-line no-return-assign, no-sequences
        (s, p) => (el.style[p] && (s[p] = el.style[p]), s),
        {} as Record<string, string>,
      )
    : {};

/**
 * FUNCTION: getAdjacentStyles
 * PURPOSE: Collects all formatting styles from surrounding text (ancestors and siblings)
 *
 * WHY: When a chip is placed in formatted text, it should inherit ALL formatting from:
 * - Parent elements (e.g., if inside a colored div)
 * - Previous/next sibling text (e.g., if between bold words)
 * This ensures the chip visually matches its context
 *
 * @param node - The chip node to check surroundings for
 * @returns Combined styles object from all adjacent formatted elements
 */
const getAdjacentStyles = (node: any) => {
  const styles: Record<string, string> = {};
  const ancestors: any[] = [];
  let parent = node.parentElement;

  //  Collect all parent elements up to BODY
  // Parents may have formatting that should cascade down to the chip
  while (parent && parent.tagName !== 'BODY') {
    if (parent.tagName === 'SPAN' && !parent.hasAttribute('data-mce-bogus')) {
      ancestors.unshift(parent);
    }
    parent = parent.parentElement;
  }
  // Extract and merge styles from all ancestors
  ancestors.forEach((ancestor) => Object.assign(styles, getStyles(ancestor)));

  // Also check siblings (text before and after the chip)
  // If text immediately before/after is formatted, chip should match
  [node.previousSibling, node.nextSibling].forEach((sibling) => {
    if (sibling && !sibling.hasAttribute?.('data-mce-bogus')) {
      Object.assign(styles, getStyles(sibling));
    }
  });

  return styles;
};

/**
 * FUNCTION: hasTag
 * Checks if a node or its ancestors have a specific HTML tag (like <strong>, <em>, <u>)
 * To detect if text is bold/italic/underlined by checking for formatting tags in the DOM tree
 *
 *  The node to start checking from
 * The HTML tag name to look for (e.g., 'strong', 'em', 'u')
 *  The root element to stop checking at (usually the editor body)
 * @returns true if the tag is found in the ancestor chain, false otherwise
 *
 */
const hasTag = (node: any, tag: string, root: any): boolean => {
  let el = node?.nodeType === 1 ? node : node?.parentElement;
  while (el && el !== root) {
    if (el.tagName?.toLowerCase() === tag) return true;
    el = el.parentElement;
  }
  return false;
};

/**
 * FUNCTION: hasAdjacentTag
 * Checks if a chip's siblings or parent have a specific formatting tag
 *
 *  If text next to a chip is bold/italic/underlined, the chip should also be formatted
 * to maintain visual consistency
 *
 *  The chip element to check
 * The HTML tag to look for (e.g., 'strong', 'em', 'u')
 *  The root element (editor body)
 * return true if adjacent text has the formatting tag
 *
 * EXAMPLE: If chip is between <strong>Bold text</strong> and normal text,
 * hasAdjacentTag(chip, 'strong', body) returns true
 */
const hasAdjacentTag = (chip: any, tag: string, root: any) => {
  const check = (s: any) =>
    s?.nodeType === 1
      ? s.tagName?.toLowerCase() === tag
      : s?.textContent?.trim() && hasTag(s, tag, root);

  return (
    check(chip.previousSibling) ||
    check(chip.nextSibling) ||
    chip.parentElement?.tagName?.toLowerCase() === tag
  );
};

/**
 * FUNCTION: wrapChip
 * Wraps a chip element with either a formatting tag or a styled span
 *
 *  To apply formatting to chips, we wrap them in HTML tags (like <strong>) or
 * styled <span> elements. This makes the chip inherit the visual formatting.
 *
 * The chip element to wrap
 *  Either a tag name (string) like 'strong' or a styles object
 *
 * If chip is already inside a single-child span, we just update its styles
 * instead of creating a new wrapper (avoids nested spans)
 *
 */
const wrapChip = (chip: any, tagOrStyles: string | Record<string, string>) => {
  const parent = chip.parentElement;
  const isStyle = typeof tagOrStyles === 'object';

  // Reuse existing span parent if it only contains this one chip
  if (isStyle && parent?.tagName === 'SPAN' && parent.childNodes.length === 1) {
    Object.assign(parent.style, tagOrStyles);
  } else {
    // Create new wrapper element (either formatting tag or styled span)
    const wrapper = document.createElement(
      isStyle ? 'span' : (tagOrStyles as string),
    );
    if (isStyle) Object.assign(wrapper.style, tagOrStyles);
    chip.parentNode?.insertBefore(wrapper, chip);
    wrapper.appendChild(chip);
  }
};


/**
 * FUNCTION: applyChipFormatting (Internal Core Function)
 * The main function that applies all formatting to chips in the editor
 *
 * WHY: This is the heart of the formatting system. It:
 * 1. Finds all chips in the editor
 * 2. Ensures they're properly spaced
 * 3. Applies inline styles from surrounding text
 * 4. Wraps them in formatting tags (bold/italic/underline)
 * 5. Optimizes the DOM by merging adjacent spans with identical styles
 *
 * WHEN CALLED: After any formatting change, text input, or editor update
 *
 * The TinyMCE editor body element
 * The TinyMCE editor instance
 *
 * WORKFLOW:
 * Step 1: Process each chip for spacing and style inheritance
 * Step 2: Apply formatting tags (bold/italic/underline) to chips
 * Step 3: Clean up DOM by merging redundant spans
 */
const applyChipFormatting = (body: any, editor: any) => {
  body.querySelectorAll(CHIP_SELECTOR).forEach((chip: any) => {
    // STEP 1: Ensure chips are properly spaced apart

    // STEP 2: Apply inline styles (color, font, size, etc.) to chips
    const styles = getAdjacentStyles(chip);
    if (Object.keys(styles).length) {
      // Apply styles directly to the chip element
      TRACKED_STYLES.forEach(
        // eslint-disable-next-line no-return-assign
        (prop) => styles[prop] && (chip.style[prop] = styles[prop]),
      );

      // IMPORTANT: Also apply styles to inner chip content elements
      // The chip container has nested spans (.merge-tag-chip, .variable-tag-chip, etc.)
      // that need to inherit the formatting styles
      const innerElements = chip.querySelectorAll(
        '.merge-tag-chip, .variable-tag-chip, .spin-tag-chip, .date-tag-chip, .error-chip',
      );
      innerElements.forEach((innerEl: any) => {
        TRACKED_STYLES.forEach(
          // eslint-disable-next-line no-return-assign
          (prop) => styles[prop] && (innerEl.style[prop] = styles[prop]),
        );
      });

      // Also update parent span if chip is the only child (optimization)
      const parent = chip.parentElement;
      if (parent?.tagName === 'SPAN' && parent.childNodes.length === 1) {
        Object.assign(parent.style, styles);
      } else if (!parent || parent.tagName !== 'SPAN') {
        // Or wrap chip in new styled span
        wrapChip(chip, styles);
      }
    }

    // STEP 3: Apply formatting tags (bold, italic, underline) to chips
    Object.values(FORMAT_TAGS).forEach((tag) => {
      if (
        chip.parentElement?.tagName?.toLowerCase() !== tag &&
        hasAdjacentTag(chip, tag, body)
      ) {
        wrapChip(chip, tag);
      }
    });
  });

  // STEP 4: DOM OPTIMIZATION - Merge adjacent spans with identical styles
  // WHY: Multiple formatting operations can create many nested/adjacent spans.
  // This cleans up the DOM by combining spans with the same styles.
  // IMPORTANT: We preserve chips and never merge spans that contain chips
  body.querySelectorAll('span').forEach((span: HTMLElement) => {
    const next = span.nextElementSibling as HTMLElement;
    const [s1, s2] = [getStyles(span), getStyles(next)];
    const hasOrIsChip = (el: HTMLElement) =>
      el?.querySelector(CHIP_SELECTOR) || el?.matches?.(CHIP_SELECTOR);

    // If adjacent spans have identical styles and neither contains chips, merge them
    if (
      next?.tagName === 'SPAN' &&
      Object.keys(s1).length &&
      JSON.stringify(s1) === JSON.stringify(s2) &&
      !hasOrIsChip(span) &&
      !hasOrIsChip(next)
    ) {
      // Move all children from next span to current span
      while (next.firstChild) span.appendChild(next.firstChild);
      next.remove();
    }
  });
};

/**
 * EXPORTED FUNCTION: applyFormattingToChips
 * Public API to apply all formatting to chips in the editor
 *
 * This is the main function called from outside this file to trigger
 * the formatting synchronization process
 *
 * @param editor - The TinyMCE editor instance
 */
export const applyFormattingToChips = (editor: any) => {
  const body = editor.getBody();
  if (body) applyChipFormatting(body, editor);
};

/**
 * FUNCTION: clearChipFormatting (Internal)
 * PURPOSE: Removes all formatting wrappers from chips
 *
 * When user clicks "Clear Formatting", chips should also have their
 * formatting removed (unwrapped from <strong>, <em>, <u> tags)
 *
 * WHAT IT DOES:
 * 1. Finds all chips wrapped in formatting tags
 * 2. Unwraps them by moving chip out of the formatting tag
 * 3. Removes empty formatting tags
 * 4. Cleans up any remaining data attributes
 */
const clearChipFormatting = (editor: any) => {
  editor
    .getBody()
    ?.querySelectorAll(CHIP_SELECTOR)
    .forEach((chip: any) => {
      // Remove formatting tags (strong, em, u) from around chips
      Object.values(FORMAT_TAGS).forEach((tag) => {
        const parent = chip.parentElement;
        if (parent?.tagName?.toLowerCase() === tag && parent.parentElement) {
          // Move chip out of formatting tag
          parent.parentElement.insertBefore(chip, parent);
          // Remove empty formatting tag
          if (!parent.childNodes.length) parent.remove();
        }
      });
    });

  // Clean up any leftover data attributes (except TinyMCE's own)
  setTimeout(() => {
    const content = editor.getContent();
    const cleaned = content?.replace(/\sdata-(?!mce-)[^=]*="[^"]*"/gi, '');
    if (cleaned && cleaned !== content) editor.setContent(cleaned);
  }, 50);
};

/**
 * EXPORTED FUNCTION: setupChipFormattingListeners
 * PURPOSE: Sets up event listeners to automatically apply formatting to chips
 *
 * WHY: This makes the formatting process automatic. Once set up, chips will
 * automatically sync their formatting whenever the user makes changes.
 *
 * @param editor - The TinyMCE editor instance
 *
 * WHEN TO CALL: Once during editor initialization
 *
 * WHAT IT LISTENS FOR:
 * 1. Style Commands (color, font, size changes) - applies formatting after 150-400ms
 * 2. Format Toggle (bold, italic, underline) - applies formatting after 100ms
 * 3. Remove Format command - clears all chip formatting
 * 4. General changes (input, change, node change) - applies formatting after 10ms
 *
 * TIMING: Uses setTimeout to ensure TinyMCE's own formatting completes first
 */
export const setupChipFormattingListeners = (editor: any) => {
  // List of TinyMCE commands that apply inline styles
  const styleCommands = [
    'mceApplyTextcolor',
    'ForeColor',
    'BackColor',
    'FontSize',
    'FontName',
  ];

  // Flag to prevent double-processing during style application
  let isApplyingStyle = false;

  // LISTENER 1: Handle TinyMCE commands (formatting actions)
  editor.on('ExecCommand', (e: any) => {
    if (styleCommands.includes(e.command)) {
      // CASE 1: Style commands (color, font changes)
      // WHY TWO TIMEOUTS: TinyMCE processes styles in multiple passes
      // First at 150ms for immediate visual update, then at 400ms to catch delayed changes
      isApplyingStyle = true;
      setTimeout(() => {
        applyFormattingToChips(editor);
        isApplyingStyle = false;
      }, 150);
      setTimeout(() => applyFormattingToChips(editor), 400);
    } else if (e.command === 'mceToggleFormat') {
      // CASE 2: Format toggle commands (bold, italic, underline)
      // WHY 100ms: These commands apply faster than style commands
      isApplyingStyle = true;
      setTimeout(() => {
        applyFormattingToChips(editor);
        isApplyingStyle = false;
      }, 100);
    } else if (e.command === 'RemoveFormat') {
      // CASE 3: Clear formatting command
      // Remove all formatting from chips
      clearChipFormatting(editor);
    }
  });

  // LISTENER 2: Handle general editor changes (typing, pasting, etc.)
  // WHY CHECK isApplyingStyle: Prevent recursive/duplicate processing
  // when formatting is already being applied by the command listener above
  editor.on('NodeChange input change', () => {
    if (!isApplyingStyle) setTimeout(() => applyFormattingToChips(editor), 10);
  });
};
